// Copyright (c) AdCritter. All rights reserved.
import PageContent from 'pages/MasterCampaignPlanPage/new/components/PageContent'
import React, { FC, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'

import { EuiTabbedContent, EuiLoadingContent, EuiTitle, EuiSpacer } from '@elastic/eui'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { useGetAIPlanQuery } from 'api/aiPlansApi'
import { useGetBillboardsByZipCodesQuery } from 'api/billboardApi'
import { useGetDemographicAndHousingCharacteristicsQuery, useGetEducationalAttainmentQuery, useGetFinancialCharacteristicsQuery } from 'api/censusDataApi'
import { useLazyGetDomainsQuery } from 'api/domainsApi'
import { MasterCampaignStub } from 'api/entities/MasterCampaignStub'
import { useBillboardZipCodes, useHouseholdZipCodes } from 'utils/useProjectionsUtils'

import AIPlanExecutiveSummary from './components/AIPlanExecutiveSummary'
import AIPlanMarketAnalysis from './components/AIPlanMarketAnalysis'
import AIPlanMediaPlan from './components/AIPlanMediaPlan'
import AIPlanPersonas from './components/AIPlanPersonas'
import AIPlanProjections from './components/AIPlanProjections'

interface AIPlanPreviewPageParams {
  id: string
}

const AIPlanPreviewPage: FC = () => {
  const { id } = useParams<AIPlanPreviewPageParams>()

  const { data: aiPlan, isFetching } = useGetAIPlanQuery({ publicId: id ?? '' }, { skip: !id })

  const aiPlanStub = (aiPlan?.aiPlanStub ?? undefined) as unknown as MasterCampaignStub | undefined

  const billboardZipCodes = useBillboardZipCodes(aiPlanStub)
  const householdZipCodes = useHouseholdZipCodes(aiPlanStub)

  useGetBillboardsByZipCodesQuery({ zipCodes: billboardZipCodes }, { skip: billboardZipCodes.length === 0 })
  useGetDemographicAndHousingCharacteristicsQuery({ zipCodes: householdZipCodes }, { skip: householdZipCodes.length === 0 })
  useGetEducationalAttainmentQuery({ zipCodes: householdZipCodes }, { skip: householdZipCodes.length === 0 })
  useGetFinancialCharacteristicsQuery({ zipCodes: householdZipCodes }, { skip: householdZipCodes.length === 0 })

  const [triggerGetDomains, domainsResult] = useLazyGetDomainsQuery()

  useEffect(() => {
    if (!aiPlanStub) {
      return
    }

    triggerGetDomains()
  }, [aiPlanStub])

  useEffect(() => {
    if (!domainsResult?.data || domainsResult.data.length <= 0) {
      return
    }

    for (const domain of domainsResult.data) {
      new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(domain.logoUrl)
        img.onerror = () => reject()

        img.src = domain.logoUrl
      })
    }
  }, [domainsResult])

  if (!aiPlan) {
    if (isFetching) {
      return <EuiLoadingContent />
    }
    return (
      <EuiTitle>
        <h2>Plan not found</h2>
      </EuiTitle>
    )
  }

  const getTabFontSize = () => {
    return window.innerWidth > 1102 ? '16px' : '13px'
  }

  const projectionsDisabled = !aiPlan.tvNetworkDistribution || !aiPlan.internetMarginOfError || !aiPlan.tvMarginOfError || !aiPlan.billboardMarginOfError || !aiPlan.aiPlanStub?.campaignBuilders?.some(cb => cb.cpm)

  const allTabs: EuiTabbedContentTab[] = [
    {
      id: 'executive-summary',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Executive Summary</div>
        </div>
      ),
      content: (
        <PageContent>
          <AIPlanExecutiveSummary aiPlan={aiPlan} />
        </PageContent>
      )
    },
    {
      id: 'market-analysis',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Analysis & Strategy</div>
        </div>
      ),
      content: (
        <PageContent>
          <AIPlanMarketAnalysis aiPlan={aiPlan} />
        </PageContent>
      )
    },
    {
      id: 'personas',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Personas</div>
        </div>
      ),
      content: (
        <PageContent>
          <AIPlanPersonas aiPlan={aiPlan} />
        </PageContent>
      )
    },
    {
      id: 'media-plan',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Media Plan</div>,
      content: (
        <PageContent>
          <AIPlanMediaPlan aiPlan={aiPlan} />
        </PageContent>
      )
    },
    {
      id: 'projections',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Projections</div>,
      disabled: projectionsDisabled,
      content: (
        <PageContent>
          <AIPlanProjections aiPlan={aiPlan} />
        </PageContent>
      )
    }
  ]

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'executive-summary' && aiPlan.clientPreviewShowExecutiveSummary === false) return false
    if (tab.id === 'market-analysis' && aiPlan.clientPreviewShowMarketAnalysis === false) return false
    if (tab.id === 'personas' && aiPlan.clientPreviewShowPersonas === false) return false
    if (tab.id === 'media-plan' && aiPlan.clientPreviewShowMediaPlan === false) return false
    if (tab.id === 'projections' && aiPlan.clientPreviewShowProjections === false) return false
    return true
  })

  return (
    <React.Fragment>
      <Helmet>
        <title>Plan - {aiPlan.name}</title>
      </Helmet>

      <EuiTitle>
        <h2>{aiPlan.name}</h2>
      </EuiTitle>

      <EuiSpacer />

      <EuiTabbedContent tabs={tabs} initialSelectedTab={tabs[0]} />
    </React.Fragment>
  )
}

export default AIPlanPreviewPage
